import { useState, type CSSProperties } from 'react';
import { CurrencyFlag, type CurrencyCode } from '~/components/ui/CurrencyFlag';
import { cn } from '~/lib/cn';

const CARD_W = 320;
const CARD_RADIUS = 40;
const ROW_H = 64; // row slot = selector height
const ROW_GAP = 8; // vertical gap between rows
const ROW_STEP = ROW_H + ROW_GAP; // distance between consecutive row slots
const PAD = 16; // card inner padding (matches the highlight inset)
const REVEAL_MS = 700;
const REVEAL_DELAY_MS = 0;
const REVEAL_DIAMETER = 64;
const balances: { code: CurrencyCode; amount: string }[] = [
  { code: 'USD', amount: '$6,523.00' },
  { code: 'GBP', amount: '£4,291.00' },
  { code: 'EUR', amount: '€2,871.39' },
];
const CARD_BODY_H = ROW_H * balances.length + ROW_GAP * (balances.length - 1);
const CARD_H = CARD_BODY_H + PAD * 2;

/**
 * Floating multi-currency balances card, centred over the hero image.
 *
 * - Reveals from a centred circle into the rounded card.
 * - Interactive: a soft highlight glides between rows on hover/focus.
 */
export function HeroBalanceCard({ revealed }: { revealed: boolean }) {
  const [active, setActive] = useState(0);

  return (
    <div
      style={
        {
          width: CARD_W,
          borderRadius: CARD_RADIUS,
          padding: PAD,
          '--hero-card-ms': `${REVEAL_MS}ms`,
          '--hero-card-delay': `${REVEAL_DELAY_MS}ms`,
          '--hero-card-radius': `${CARD_RADIUS}px`,
          '--hero-card-start-scale-x': REVEAL_DIAMETER / CARD_W,
          '--hero-card-start-scale-y': REVEAL_DIAMETER / CARD_H,
        } as CSSProperties
      }
      className={cn(
        'hero-card pointer-events-auto max-w-full',
        revealed ? 'hero-card--revealed' : 'hero-card--pending',
      )}>
      <div className="hero-card__body relative" style={{ height: CARD_BODY_H }}>
        {/* Highlight glides to the active row and stays where the cursor left. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 rounded-full bg-grey-50 transition-transform duration-200 ease-nav"
          style={{ height: ROW_H, transform: `translateY(${active * ROW_STEP}px)` }}
        />

        {balances.map((b, i) => (
          <button
            key={b.code}
            type="button"
            onMouseEnter={() => setActive(i)}
            onFocus={() => setActive(i)}
            style={{ top: i * ROW_STEP, height: ROW_H }}
            className="absolute inset-x-0 flex items-center gap-[10px] rounded-full px-4 text-left">
            <CurrencyFlag code={b.code} className="size-8" />
            <span className="flex flex-col gap-[10px]">
              <span className="text-[14px] font-semibold leading-tight text-ink">{b.code}</span>
              <span className="text-[14px] font-medium leading-tight text-grey-900">
                {b.amount}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
